import React from 'react';
import { LucideIcon } from 'lucide-react';
import { BaseCard } from '@/components/shared/BaseCard';
import { MetricsGrid } from '@/components/shared/MetricsGrid';
import { ActivityList } from '@/components/shared/ActivityList';
import { InsightsList } from '@/components/shared/InsightsList';
import { DashboardData } from '@/services/DashboardService';
import { cn } from '@/lib/utils';

interface DashboardWidgetProps {
  title: string;
  description: string;
  icon: LucideIcon;
  primaryColor: string;
  gradientFrom?: string; // Legacy prop for backward compatibility
  gradientTo?: string; // Legacy prop for backward compatibility
  ctaText: string;
  ctaLink: string;
  data: DashboardData;
  badges?: string[];
  className?: string;
}

export const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  title,
  description,
  icon,
  primaryColor,
  ctaText,
  ctaLink,
  data,
  badges = [],
  className
}) => {
  // Transform data for shared components
  const metrics = data.metrics.map(metric => ({
    label: metric.label,
    value: metric.value,
    icon: metric.icon,
    color: metric.color
  }));

  const activities = data.recentActivities.map(activity => ({
    type: activity.type,
    details: activity.details,
    date: activity.date
  }));

  const insights = data.insights.map(insight => ({
    text: insight.text,
    type: insight.type as 'positive' | 'warning' | 'info' | 'success'
  }));

  return (
    <BaseCard
      title={title}
      description={description}
      icon={icon}
      badges={badges}
      variant="gradient"
      colorScheme={primaryColor}
      className={className}
      actions={{
        primary: {
          text: ctaText,
          link: ctaLink,
          icon: icon
        }
      }}
    >
      <div className="space-y-4">
        {/* Performance Metrics */}
        <MetricsGrid 
          metrics={metrics}
          columns={3}
          variant="glass"
        />

        {/* Recent Activities */}
        {activities.length > 0 && (
          <ActivityList 
            activities={activities}
            variant="default"
            maxItems={3}
          />
        )}

        {/* Insights */}
        {insights.length > 0 && (
          <InsightsList 
            insights={insights}
            variant="compact"
            maxItems={2}
          />
        )}

        {/* Next Suggestion */}
        {data.nextSuggestion && (
          <div className={cn(
            "p-3 rounded-lg border",
            primaryColor === 'accent'
              ? 'bg-gradient-to-r from-accent/10 to-accent/5 border-accent/20'
              : 'bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20'
          )}>
            <div className="flex items-start gap-2">
              <div className={cn(
                "p-1 rounded-full",
                primaryColor === 'accent'
                  ? 'bg-gradient-to-r from-accent to-accent/80 text-accent-foreground'
                  : 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground'
              )}>
                {React.createElement(icon, { className: "h-3 w-3" })}
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium mb-1">Next Step</p>
                <p className="text-xs text-muted-foreground">{data.nextSuggestion}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </BaseCard>
  );
};